import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, CircularProgress, Dialog, DialogContent, Divider, IconButton, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import React, { useState } from 'react';
import { useWorkOrderHistory } from '../../../services/business/workOrders';
import { USER_TIMEZONE, WOA_ACTION, WOA_HISTORY_TITLE } from '../../../utility/Constant';
import SortArrowsSvg from '../../../utility/Svg/SortArrowsSvg';
import {
    WoaAuthorizationConfirmedSvg,
    WoaAuthorizationRemovedSvg,
    WoaCancellationRemovedSvg,
    WoaCancelledSvg,
    WoaConvertedToInvoiceSvg,
    WoaCreatedFromEstimateSvg,
    WoaCreatedSvg,
    WoaEditedSvg,
    WoaInvoiceUnlinkedSvg,
    WoaPdfDownloadedSvg,
    WoaReauthorizedSvg,
    WoaEmailSentSvg,
} from '../../../utility/Svg/WorkOrderHistoryIcons';

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Activity → icon. The backend sends `action` plus `isEdited`, and the same code means different
 * things in each case (e.g. `draft` is "Saved as draft" vs "Edited and saved as draft"), so the icon
 * is chosen from both rather than by reading the prose title.
 */
const STANDARD_ICON = {
    [WOA_ACTION.DRAFT]: WoaCreatedSvg,
    [WOA_ACTION.CREATE]: WoaCreatedSvg,
    [WOA_ACTION.CREATE_FROM_ESTIMATE]: WoaCreatedFromEstimateSvg,
    [WOA_ACTION.UPDATE]: WoaEditedSvg,
    [WOA_ACTION.PRINT]: WoaPdfDownloadedSvg,
    [WOA_ACTION.DOWNLOAD]: WoaPdfDownloadedSvg,
    [WOA_ACTION.AUTHORISED]: WoaAuthorizationConfirmedSvg,
    [WOA_ACTION.REAUTHORISED]: WoaReauthorizedSvg,
    [WOA_ACTION.DECLINED]: WoaCancelledSvg,
    [WOA_ACTION.INVOICE_DELETED]: WoaInvoiceUnlinkedSvg,
    [WOA_ACTION.CONVERTED_TO_INVOICE]: WoaConvertedToInvoiceSvg,
    [WOA_ACTION.EMAIL_SENT]: WoaEmailSentSvg,
    [WOA_ACTION.EMAIL_RESENT]: WoaEmailSentSvg,
};

const EDITED_ICON = {
    [WOA_ACTION.DRAFT]: WoaEditedSvg,
    [WOA_ACTION.UPDATE]: WoaEditedSvg,
    [WOA_ACTION.PRINT]: WoaPdfDownloadedSvg,
    [WOA_ACTION.REAUTHORIZATION]: WoaAuthorizationRemovedSvg,
    [WOA_ACTION.CANCELLATION_REMOVED]: WoaCancellationRemovedSvg,
    [WOA_ACTION.INVOICE_UNLINKED]: WoaInvoiceUnlinkedSvg,
    [WOA_ACTION.INVOICE_DELETED]: WoaInvoiceUnlinkedSvg,
};

/** Picks the edited set when `isEdited` is true, falling back to the standard set for any code it lacks. */
const iconForEntry = (entry) => {
    const edited = entry?.isEdited === true;
    const Icon = (edited && EDITED_ICON[entry?.action]) || STANDARD_ICON[entry?.action] || WoaCreatedSvg;
    console.log('🚀 ~ iconForEntry ~ Icon:', edited, entry?.action);
    return <Icon />;
};

/** The API's own title, else the backend-defined title for this action and edit state. */
const titleForEntry = (entry) => {
    if (entry?.title) return entry.title;
    const edited = entry?.isEdited === true;
    return (edited && WOA_HISTORY_TITLE.edited[entry?.action]) || WOA_HISTORY_TITLE.standard[entry?.action] || '';
};

/**
 * Work Order authorization history.
 *
 * Self-contained: give it a work order id and it fetches, sorts and renders its own
 * history, so any screen can open it — the listing row menu, the detail page's More
 * Actions, or anywhere else (spec §14.9).
 */
const WorkOrderHistoryModal = ({ open, onClose, workOrderId }) => {
    // The API sorts, so the toggle is just a param — no client-side re-sorting.
    const [sortType, setSortType] = useState('DSC');

    const { data: history, isFetching } = useWorkOrderHistory(workOrderId, { sortType }, { enabled: open && !!workOrderId });

    const entries = Array.isArray(history) ? history : [];
    const isNewestFirst = sortType === 'DSC';

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            PaperProps={{
                sx: {
                    borderRadius: '8px',
                    // The dialog hugs its content, so a short history is a short modal. A minimum
                    // keeps it from resizing between the loader and a small list, and the maximum
                    // makes a long history scroll inside the body instead of growing the modal.
                    minHeight: '260px',
                    maxHeight: 'min(520px, calc(100vh - 64px))',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                },
            }}
        >
            <Box
                sx={{
                    flex: '0 0 auto',
                    height: '64px',
                    padding: '0 24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '16px',
                    borderRadius: '8px 8px 0 0 !important',
                    background: '#002394',
                    boxShadow: '0 1px 10px 0 rgba(0, 0, 0, 0.12), 0 4px 5px 0 rgba(0, 0, 0, 0.14), 0 2px 4px -1px rgba(0, 0, 0, 0.20)',
                }}
            >
                <Typography
                    sx={{
                        color: '#FFF',
                        fontFeatureSettings: "'liga' off, 'clig' off",
                        fontSize: '20px',
                        fontWeight: 500,
                        lineHeight: '160%',
                        letterSpacing: '0.15px',
                    }}
                >
                    History
                </Typography>
                <IconButton onClick={onClose} sx={{ color: '#FFF', padding: 0 }} aria-label="Close history">
                    <CloseIcon />
                </IconButton>
            </Box>

            <DialogContent sx={{ flex: '1 1 auto', padding: '2px', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <Box sx={{ padding: '14px 22px 0' }}>
                    {/* Count + sort toggle */}
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ paddingBottom: '12px' }}>
                        <Typography sx={{ fontSize: '14px', color: 'rgba(0, 0, 0, 0.60)', letterSpacing: '0.15px', lineHeight: '150%' }}>
                            {entries.length} {entries.length === 1 ? 'Activity' : 'Activities'}
                        </Typography>

                        <Button
                            size="small"
                            endIcon={<SortArrowsSvg />}
                            onClick={() => setSortType(isNewestFirst ? 'ASC' : 'DSC')}
                            sx={{
                                fontSize: '14px',
                                fontWeight: 400,
                                color: '#666666',
                                textTransform: 'none',
                                lineHeight: '24px' /* 171.429% */,
                                letterSpacing: '0.4px',
                            }}
                        >
                            {isNewestFirst ? 'Newest First' : 'Oldest First'}
                        </Button>
                    </Stack>

                    <Divider sx={{ borderColor: 'rgba(0, 0, 0, 0.12)' }} />
                </Box>

                {/* Scrolls once the history outgrows the maximum body height */}
                <Box sx={{ flex: '1 1 auto', overflowY: 'auto', padding: '20px 22px 22px' }}>
                    {isFetching ? (
                        <Stack alignItems="center" justifyContent="center" sx={{ flex: '1 1 auto', minHeight: '140px' }}>
                            <CircularProgress />
                        </Stack>
                    ) : entries.length === 0 ? (
                        <Typography sx={{ color: 'rgba(0,0,0,0.6)' }}>No activity yet.</Typography>
                    ) : (
                        <Stack gap={3}>
                            {entries.map((entry, index) => (
                                <Stack key={`${entry?.date}-${index}`} gap={0.5}>
                                    <Stack direction="row" alignItems="center" gap={0.5}>
                                        {iconForEntry(entry)}
                                        <Typography sx={{ fontSize: '16px', fontWeight: 500, letterSpacing: '0.15px', lineHeight: '150%' }}>
                                            {titleForEntry(entry)}
                                        </Typography>
                                    </Stack>

                                    <Typography sx={{ fontSize: '16px', letterSpacing: '0.15px', color: 'rgba(0, 0, 0, 0.60)', lineHeight: '150%' }}>
                                        {entry?.date ? dayjs(entry.date).tz(USER_TIMEZONE).format('DD-MMM-YYYY h:mm A') : '—'}
                                    </Typography>

                                    {entry?.description && (
                                        <Typography sx={{ fontSize: '16px', letterSpacing: '0.15px', color: 'rgba(0, 0, 0, 0.60)', lineHeight: '150%' }}>
                                            {entry.description}
                                        </Typography>
                                    )}
                                </Stack>
                            ))}
                        </Stack>
                    )}
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default WorkOrderHistoryModal;
